// Full off-platform backup — every SQL table, one JSON file (2026-07-30).
//
// The Data page's original protections (localStorage snapshots + item CSV)
// cover 4 entities from the app's inception; the business now spans ~38
// tables. This button reads EVERY table (paginated past the 1,000-row
// PostgREST cap) and downloads one timestamped JSON file — drop it in
// Dropbox and the whole database exists off Supabase.
//
// Deliberately excluded: firm_qb_credentials (server-only OAuth secrets;
// RLS denies the browser by design — a backup must never contain them).
// Storage bucket FILES (receipt images, as-sent PDFs) are separate;
// the panel copy says so.

import React, { useState } from 'react';
import { supabase } from '../lib/supabase';

// Every table the app writes, from the migrations + code inventory.
// Order column: 'id' unless the table has no id (exceptions below);
// export falls back to unordered pagination if the column is missing.
export const EXPORT_TABLES = [
  'app_storage', 'assemblies', 'billing_summaries', 'card_statement_lines',
  'card_statements', 'change_history', 'change_orders', 'client_credit_ledger',
  'client_payments', 'doc_number_sequences', 'document_sends',
  'feature_requests', 'final_invoice_items', 'firm_credit_cards',
  'firm_settings', 'hours_invoices', 'invoices', 'item_documents',
  'item_number_sequences', 'items', 'notes', 'owner_draws',
  'payment_allocations', 'proposals', 'purchase_orders',
  'purchasing_email_attachments', 'purchasing_email_items', 'purchasing_emails',
  'quotes', 'receipt_attachments', 'receipts', 'retainer_ledger',
  'schedule_templates', 'schedules', 'state_tax_records',
  'vendor_credit_ledger', 'vendor_payment_items', 'vendor_payments', 'vendors',
];
const ORDER_COL = { app_storage: 'key', doc_number_sequences: null, item_number_sequences: null, firm_settings: null };

async function fetchWholeTable(table) {
  const orderCol = Object.prototype.hasOwnProperty.call(ORDER_COL, table) ? ORDER_COL[table] : 'id';
  const rows = [];
  const PAGE = 1000;
  let from = 0;
  for (;;) {
    let q = supabase.from(table).select('*').range(from, from + PAGE - 1);
    if (orderCol) q = q.order(orderCol, { ascending: true });
    let r = await q;
    if (r.error && orderCol) {
      // unknown order column (schema drift) — retry unordered
      r = await supabase.from(table).select('*').range(from, from + PAGE - 1);
    }
    if (r.error) throw new Error(r.error.message || String(r.error));
    const page = r.data || [];
    rows.push(...page);
    if (page.length < PAGE) return rows;
    from += PAGE;
  }
}

export default function FullExportPanel() {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState('');
  const [result, setResult] = useState(null);

  async function exportEverything() {
    if (running) return;
    setRunning(true); setResult(null);
    const out = { exportedAt: new Date().toISOString(), app: 'gilda', schemaNote: 'raw rows per table, snake_case columns', tables: {}, skipped: [] };
    let rowsTotal = 0;
    for (let i = 0; i < EXPORT_TABLES.length; i++) {
      const t = EXPORT_TABLES[i];
      setProgress(`${t} (${i + 1}/${EXPORT_TABLES.length})…`);
      try {
        const rows = await fetchWholeTable(t);
        out.tables[t] = rows;
        rowsTotal += rows.length;
      } catch (e) {
        out.skipped.push({ table: t, reason: String(e.message || e) });
      }
    }
    setProgress('');
    const json = JSON.stringify(out);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'CimonStarr_FullBackup_' + new Date().toISOString().slice(0, 10) + '.json';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setResult({ tables: Object.keys(out.tables).length, rows: rowsTotal, skipped: out.skipped, size: json.length });
    setRunning(false);
  }

  return (
    <div style={{ background: '#fff', border: '1px solid #E0E0DC', borderRadius: 10, padding: 20, marginBottom: 20 }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: '#111111', marginBottom: 6 }}>⬇ Download EVERYTHING — full database backup</div>
      <div style={{ fontSize: 12, color: '#555552', marginBottom: 12, lineHeight: 1.5 }}>
        Every table — items, invoices, payments, receipts, POs, proposals, ledgers, billing history,
        schedules, settings — in one JSON file. Save it to Dropbox monthly (or before anything risky):
        it is your copy of the business that lives outside Supabase. Files in storage
        (receipt images, as-sent PDFs) are not included — they need the separate bucket backup.
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={exportEverything} disabled={running}
          style={{ padding: '8px 20px', background: running ? '#888880' : '#111111', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: running ? 'default' : 'pointer' }}>
          {running ? 'Exporting ' + progress : '⬇ Download full backup'}
        </button>
        {result && (
          <div style={{ fontSize: 11.5, color: result.skipped.length ? '#665518' : '#1A5C38' }}>
            {result.tables} tables · {result.rows.toLocaleString()} rows · {(result.size / 1048576).toFixed(1)} MB
            {result.skipped.length > 0 && (' · skipped: ' + result.skipped.map((s) => s.table).join(', '))}
          </div>
        )}
      </div>
    </div>
  );
}
